import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { useDispatch, useSelector, type TypedUseSelectorHook } from "react-redux";
import { store, type RootState, type AppDispatch } from "lib/redux/store";
import { loadStateFromLocalStorage, saveStateToLocalStorage } from "lib/redux/local-storage";
import { initialResumeState, setResume } from "lib/redux/resumeSlice";
import { initialSettings, setSettings, type Settings } from "lib/redux/settingsSlice";
import { deepMerge } from "lib/deep-merge";
import type { Resume } from "lib/redux/types";
import { initialUIState, setUI, UIState, setAccessToken } from "./uiSlice";
import { initialScoreState, ScoreState, setScoring } from "./scoringSlice";
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Hook to save store to local storage on store change
 */
export const useSaveStateToLocalStorageOnChange = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  useEffect(_temp2, t0);
};
export const useSetInitialStore = () => {
  const $ = _c(3);
  const dispatch = useAppDispatch();
  let t0;
  if ($[0] !== dispatch) {
    t0 = () => {
      const directToken = typeof window !== "undefined" ? localStorage.getItem("access-token") : null;
      if (directToken) {
        dispatch(setAccessToken(directToken));
      }
      const state = loadStateFromLocalStorage();
      if (!state) {
        return;
      }
      if (state.resume) {
        const mergedResumeState = deepMerge(initialResumeState, state.resume) as Resume;
        dispatch(setResume(mergedResumeState));
      }
      if (state.settings) {
        const mergedSettingsState = deepMerge(initialSettings, state.settings) as Settings;
        mergedSettingsState.isImportComponentOpen = false;
        dispatch(setSettings(mergedSettingsState));
      }
      if (state.ui) {
        const mergedUIState = deepMerge(initialUIState, state.ui) as UIState;
        mergedUIState.isAuthenticationOpen = false;
        mergedUIState.isExplorePlansOverlayOpen = false;
        mergedUIState.isOutOfCreditsModalOpen = false;
        mergedUIState.isScoringLoadingModalOpen = false;
        mergedUIState.isResumeDownloading = false;
        mergedUIState.accessToken = localStorage.getItem("access-token");
        dispatch(setUI(mergedUIState));
      }
      if (state.scoring) {
        const mergedScoringState = deepMerge(initialScoreState, state.scoring) as ScoreState;
        dispatch(setScoring(mergedScoringState));
      }
    };
    $[0] = dispatch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useEffect(t0, t1);
};
function _temp() {
  saveStateToLocalStorage(store.getState());
}
function _temp2() {
  const unsubscribe = store.subscribe(_temp);
  return unsubscribe;
}